import { useCallback, useMemo, useState } from 'react';
import {
  isAutocompletePopperClick,
  isPersonInSelection,
  mapResidentToAutocompleteOption,
} from '../../../../utils/case-plan-create.util';
import { useResidentSearchHook } from './useResidentSearchHook';

/** At 2+ selections, cards switch to the two-column grid layout (Figma 967:22953). */
const MULTI_RESIDENT_GRID_THRESHOLD = 2;

/**
 * State, search, and selection logic for the multi-select Residents section.
 * Keeps ResidentSection purely presentational.
 * @param {object[]} selectedResidents
 * @param {(residents: object[]) => void} onResidentsChange
 */
export const useResidentSectionHook = (selectedResidents, onResidentsChange) => {
  const { options, isLoading, handleSearchChange } = useResidentSearchHook();
  const [isSearchOpen, setIsSearchOpen] = useState(
    () => selectedResidents.length === 0,
  );

  const useGridLayout =
    selectedResidents.length >= MULTI_RESIDENT_GRID_THRESHOLD;

  const memberOptions = useMemo(
    () => options.map(mapResidentToAutocompleteOption).filter(Boolean),
    [options],
  );

  const selectedMembers = useMemo(
    () => selectedResidents.map(mapResidentToAutocompleteOption).filter(Boolean),
    [selectedResidents],
  );

  const handleMembersChange = useCallback(
    (nextValue) => {
      onResidentsChange(Array.isArray(nextValue) ? nextValue : []);
    },
    [onResidentsChange],
  );

  const handleRemoveResident = useCallback(
    (residentToRemove) => {
      const nextResidents = selectedResidents.filter(
        (resident) => resident.personId !== residentToRemove.personId,
      );
      onResidentsChange(nextResidents);
      if (nextResidents.length === 0) {
        setIsSearchOpen(true);
      }
    },
    [onResidentsChange, selectedResidents],
  );

  const handleAddResidentClick = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const handleSearchClickAway = useCallback(
    (event) => {
      // Portaled option list is outside this node; don't treat option picks as away.
      if (isAutocompletePopperClick(event.target)) {
        return;
      }

      // Keep search available until at least one resident is selected.
      if (selectedResidents.length === 0) {
        return;
      }

      setIsSearchOpen(false);
    },
    [selectedResidents.length],
  );

  const getOptionDisabled = useCallback(
    (option) =>
      Boolean(option.hasPlan) || isPersonInSelection(option, selectedResidents),
    [selectedResidents],
  );

  return {
    isLoading,
    isSearchOpen,
    useGridLayout,
    memberOptions,
    selectedMembers,
    handleSearchChange,
    handleMembersChange,
    handleRemoveResident,
    handleAddResidentClick,
    handleSearchClickAway,
    getOptionDisabled,
  };
};
